import "utils/polyfills";
// Must stay first so pixels and GTM never see an email in the URL.
import "utils/landingEmail";

// Libs
import React from "react";
import { createRoot } from "react-dom/client";

import { QueryClientProvider } from "@tanstack/react-query";
import { NewQueryClient } from "utils/queryClient";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { captureClickIdsFromSearch } from "utils/clickIds";
import { initOpenAIPixel } from "utils/blueTagEvents";

// Redux
import { Provider } from "react-redux";
import { store } from "utils/store/configureStore";

// Context
import { UserProvider } from "contexts/UserContext";

// Analytics Libs
import { initGoogleAnalytics } from "utils/trackEvents";
import ReactPixel from "react-facebook-pixel";
import { GoogleTagManager } from "components/GoogleTagManager";
import { AnalyticsProvider } from "use-analytics";
import { datadogRum } from "@datadog/browser-rum";
import { datadogLogs } from "@datadog/browser-logs";
import { logsBeforeSend, rumBeforeSend } from "utils/datadogNoise";
import { StatsigProvider } from "@statsig/react-bindings";

// Components
import Routes from "./routes";
import StatsigUserSync from "components/StatsigUserSync";
import MaintenanceGate from "components/MaintenanceGate";
import "utils/i18n";
// Styles
import { ThemeProvider } from "styled-components";
import theme from "components/spocketUI/theme";

// Utils
import { analytics } from "utils/analytics";
import ErrorBoundary from "utils/errorBoundary";
import registerServiceWorker from "utils/registerServiceWorker";
import { getStatsigOptions, getStatsigUser } from "utils/module/statsig";
import { installTranslationDomGuard } from "utils/translationDomGuard";

installTranslationDomGuard();
captureClickIdsFromSearch();

if (process.env.NODE_ENV === "production" || process.env.NODE_ENV === "staging") {
  initGoogleAnalytics(process.env.REACT_APP_GA_KEY);
}

// MSW - Mock Service Worker
if (process.env.NODE_ENV === "staging") {
  const { worker } = require("./mocks/browser");
  worker.start();
}

// React Facebook Pixel
ReactPixel.init(process.env.REACT_APP_FACEBOOK_PIXEL, {}, { debug: false, autoConfig: false });

// OpenAI Pixel
initOpenAIPixel();

const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
const isDevelopment = process.env.NODE_ENV === "development";
const datadogAppId = process.env.REACT_APP_DATADOG_APPLICATION_ID;
const datadogToken = process.env.REACT_APP_DATADOG_CLIENT_TOKEN;
const datadogVersion = process.env.REACT_APP_VERSION;

if (!isDevelopment && !isLocalhost && datadogAppId && datadogToken) {
  datadogRum.init({
    applicationId: datadogAppId,
    clientToken: datadogToken,
    site: process.env.REACT_APP_DATADOG_SITE || "datadoghq.com",
    service: "dropshipper-app",
    env: process.env.REACT_APP_ENV || process.env.NODE_ENV,
    version: datadogVersion,
    sessionSampleRate: 30,
    sessionReplaySampleRate: 0,
    trackUserInteractions: false,
    trackResources: false,
    trackLongTasks: true,
    defaultPrivacyLevel: "mask-user-input",
    beforeSend: event => {
      // Link scanners, crawlers, extensions, ad-blocked analytics and browser
      // notices are dropped by the shared filter; the CSP rules below are
      // specific to this app's policy.
      if (!rumBeforeSend(event)) return false;
      if (event.type !== "error") return true;
      const message = event.error?.message || "";

      // Extension injecting scripts/frames into our CSP — not fixable by us
      if (event.error?.type === "connect-src" && message.includes("'properties'")) return false;

      // CSP frame-src violations from the Joko browser extension injecting iframes
      if (event.error?.type === "frame-src" && message.includes("joko-ad-blocker-detection")) return false;

      return true;
    }
  });

  datadogLogs.init({
    clientToken: datadogToken,
    site: process.env.REACT_APP_DATADOG_SITE || "datadoghq.com",
    service: "dropshipper-app",
    env: process.env.REACT_APP_ENV || process.env.NODE_ENV,
    version: datadogVersion,
    // Off: it also forwarded every failed request to an ad-blocked analytics
    // host (700k error logs a week) and repeated what RUM already records.
    // Uncaught errors still reach RUM; console.error still becomes a log.
    forwardErrorsToLogs: false,
    forwardConsoleLogs: ["error", "warn"],
    sessionSampleRate: 100,
    beforeSend: logsBeforeSend
  });

  console.log("Datadog initialized:", { env: process.env.REACT_APP_ENV });
} else {
  console.log("Datadog disabled (development or missing credentials)");
}

// Avoiding frame busting in app
if (window.top !== window.self) {
  window.top.location = window.self.location;
}

// Load GTM (and therefore the Axon Pixel) on production and staging.
// Deployed builds run `craco build` (NODE_ENV=production); the local staging
// dev server (`npm run start:staging`) only sets REACT_APP_ENV=staging, so we
// check both. Requires a container id to avoid mounting GTM with `undefined`.
const shouldLoadGTM =
  !!process.env.REACT_APP_GTM_ID &&
  (process.env.NODE_ENV === "production" || process.env.REACT_APP_ENV === "staging");

const container = document.getElementById("root");
const root = createRoot(container);
const statsigClientKey = process.env.REACT_APP_STATSIG_CLIENT_KEY;

root.render(
  <ErrorBoundary>
    <AnalyticsProvider instance={analytics}>
      <QueryClientProvider client={NewQueryClient}>
        <Provider store={store}>
          <UserProvider>
            <StatsigProvider sdkKey={statsigClientKey} user={getStatsigUser()} options={getStatsigOptions()}>
              <StatsigUserSync />
              <ThemeProvider theme={theme}>
                {/* Axon Pixel flow: GTM mounts here, then the app pushes GA4 ecommerce
                    events (page_view, add_to_cart, begin_checkout, purchase) to the
                    dataLayer via gtmPushEcommerce(); GTM tags forward them to Axon. */}
                {shouldLoadGTM && <GoogleTagManager gtmId={process.env.REACT_APP_GTM_ID} />}

                <MaintenanceGate>
                  <Routes />
                </MaintenanceGate>
              </ThemeProvider>
            </StatsigProvider>
          </UserProvider>
        </Provider>
        {isDevelopment && <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />}
      </QueryClientProvider>
    </AnalyticsProvider>
  </ErrorBoundary>
);

registerServiceWorker();
